import { useEffect, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import { useEditor } from '../state/store';
import type { SectionGroup, SectionMetadata, BlockSchema } from '../api/types';

/**
 * Nested block tree rendered under a section row in the Sidebar.
 *   - Drag-and-drop reorder
 *   - Click to select → Inspector focuses that block
 *   - Hover: eye (toggle disabled) + trash (delete)
 *   - Bottom: "+ Adicionar <tipo>" (respects max_blocks)
 */

interface Props {
  group: SectionGroup;
  sectionId: string;
  section: SectionMetadata;
  blockSchemas: BlockSchema[];
  maxBlocks?: number;
}

function blockLabel(blockType: string, schemas: BlockSchema[]): string {
  const s = schemas.find((b) => b.type === blockType);
  if (s?.name) return s.name;
  return blockType.split(/[-_]/).map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}

function BlockIcon({ blockType }: { blockType: string }) {
  const t = blockType.toLowerCase();
  const common = 'h-[13px] w-[13px] shrink-0';
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' } as any;
  if (t.includes('image') || t.includes('slide'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>);
  if (t.includes('text') || t.includes('message') || t.includes('heading'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M4 7V5h16v2M9 5v14M15 19h-6" /></svg>);
  if (t.includes('menu') || t.includes('link'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><path d="M4 6h16M4 12h16M4 18h16" /></svg>);
  if (t.includes('button') || t.includes('cta'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="8" width="18" height="8" rx="2" /></svg>);
  if (t.includes('video'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><polygon points="5 3 19 12 5 21 5 3" /></svg>);
  if (t.includes('icon'))
    return (<svg viewBox="0 0 24 24" className={common} {...stroke}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /></svg>);
  return (<svg viewBox="0 0 24 24" className={common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /></svg>);
}

export function BlockList({ group, sectionId, section, blockSchemas, maxBlocks }: Props) {
  const listRef = useRef<HTMLUListElement>(null);
  const reorderBlocks = useEditor((s) => s.reorderBlocks);
  const removeBlock = useEditor((s) => s.removeBlock);
  const toggleBlockDisabled = useEditor((s) => s.toggleBlockDisabled);
  const addBlock = useEditor((s) => s.addBlock);
  const select = useEditor((s) => s.select);
  const selection = useEditor((s) => s.selection);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!listRef.current) return;
    const inst = Sortable.create(listRef.current, {
      animation: 160,
      handle: '[data-block-drag]',
      ghostClass: 'lc-drag-ghost',
      chosenClass: 'lc-drag-chosen',
      dragClass: 'lc-drag-drag',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        const ids = Array.from(listRef.current!.children)
          .map((li) => (li as HTMLElement).dataset.bid)
          .filter(Boolean) as string[];
        if (ids.length) reorderBlocks({ group, sectionId }, ids);
      },
    });
    return () => inst.destroy();
  }, [group, sectionId, section.blockOrder.join(','), reorderBlocks]);

  // Close the "add block" popover on outside click
  useEffect(() => {
    if (!addMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (addBtnRef.current && addBtnRef.current.contains(e.target as Node)) return;
      setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [addMenuOpen]);

  const atMax =
    typeof maxBlocks === 'number' && section.blockOrder.length >= maxBlocks;
  const addableSchemas = blockSchemas.filter((b) => b.type !== '@app' && b.type);

  return (
    <div className="relative pb-1 pt-0.5">
      {/* Vertical guide line aligned with the parent section's chevron
          column (lc-row padding 10 + ml-2 + half of w-4 = ~26px from ul
          edge). Without this the nested rows look disconnected from
          their parent row even though they're logically children. */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-2 left-[18px] top-0 w-px bg-border-strong"
      />
      <div className="pl-[30px]">
      {/* Add button — only shows when there are addable block types */}
      {addableSchemas.length > 0 && (
        <div className="relative mb-0.5">
          <button
            ref={addBtnRef}
            disabled={atMax}
            onClick={(e) => {
              e.stopPropagation();
              // If only one block type, add it directly; else open the picker
              if (addableSchemas.length === 1) {
                const id = addBlock({ group, sectionId }, addableSchemas[0].type);
                if (id) select({ kind: 'block', group, sectionId, blockId: id });
              } else {
                setAddMenuOpen((v) => !v);
              }
            }}
            className={
              'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] transition-colors ' +
              (atMax
                ? 'cursor-not-allowed text-text-disabled'
                : 'text-interactive hover:bg-interactive-subdued')
            }
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {addableSchemas.length === 1 ? (
              <span>Adicionar {blockLabel(addableSchemas[0].type, blockSchemas)}</span>
            ) : (
              <span>Adicionar bloco</span>
            )}
            {typeof maxBlocks === 'number' && (
              <span className="ml-auto text-[11px] text-text-subdued">
                {section.blockOrder.length}/{maxBlocks}
              </span>
            )}
          </button>

          {addMenuOpen && addableSchemas.length > 1 && (
            <div
              className="absolute left-0 top-full z-20 mt-1 w-full rounded-md border border-border bg-surface py-1 shadow-popover"
              onClick={(e) => e.stopPropagation()}
            >
              {addableSchemas.map((bs) => (
                <button
                  key={bs.type}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-text hover:bg-surface-hovered"
                  onClick={() => {
                    const id = addBlock({ group, sectionId }, bs.type);
                    setAddMenuOpen(false);
                    if (id) select({ kind: 'block', group, sectionId, blockId: id });
                  }}
                >
                  <BlockIcon blockType={bs.type} />
                  {blockLabel(bs.type, blockSchemas)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <ul ref={listRef} className="space-y-px">
        {section.blockOrder.map((blockId) => {
          const block = section.blocks[blockId];
          if (!block) return null;
          const disabled = block.settings?.disabled === true;
          const selected =
            selection?.kind === 'block' &&
            selection.sectionId === sectionId &&
            selection.blockId === blockId;

          // Block-level "display name" — use a setting like title/heading if present,
          // otherwise the schema name.
          const preferred =
            block.settings?.title ||
            block.settings?.heading ||
            block.settings?.message_text ||
            block.settings?.label;
          const label = preferred && String(preferred).trim().length > 0
            ? String(preferred).slice(0, 30)
            : blockLabel(block.type, blockSchemas);

          return (
            <li
              key={blockId}
              data-bid={blockId}
              data-selected={selected ? 'true' : 'false'}
              className="lc-row group"
              onClick={(e) => {
                e.stopPropagation();
                select({ kind: 'block', group, sectionId, blockId });
              }}
              title={label}
            >
              <span
                data-block-drag
                className="absolute left-1 cursor-grab text-icon-subdued opacity-0 group-hover:opacity-100"
                onClick={(ev) => ev.stopPropagation()}
                title="Arrastar para reordenar"
              >
                <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                  <circle cx="3" cy="3" r="1.1" /><circle cx="7" cy="3" r="1.1" />
                  <circle cx="3" cy="7" r="1.1" /><circle cx="7" cy="7" r="1.1" />
                  <circle cx="3" cy="11" r="1.1" /><circle cx="7" cy="11" r="1.1" />
                </svg>
              </span>
              <span className="pl-3 text-icon-subdued">
                <BlockIcon blockType={block.type} />
              </span>
              <span
                className={
                  'flex-1 truncate text-[13px] ' +
                  (disabled ? 'line-through opacity-60' : '')
                }
              >
                {label}
              </span>

              <span className="ml-1 hidden gap-0.5 group-hover:flex">
                <button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    toggleBlockDisabled({ group, sectionId, blockId });
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-icon hover:bg-surface-hovered hover:text-text"
                  title={disabled ? 'Mostrar' : 'Ocultar'}
                >
                  {disabled ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m3 3 18 18" />
                      <path d="M10.6 5.1A10 10 0 0 1 23 12a12.8 12.8 0 0 1-1.7 2.7" />
                      <path d="M6.6 6.6A13 13 0 0 0 1 12s4 8 11 8a10 10 0 0 0 4.8-1.2" />
                      <path d="M14 14.2a3 3 0 1 1-4.2-4.2" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    removeBlock({ group, sectionId, blockId });
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-icon hover:bg-critical/10 hover:text-critical"
                  title="Excluir bloco"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
                  </svg>
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      </div>
    </div>
  );
}
